/* mo-lineup-sealy.js: the Sealy look of sections/mo-brand-lineup.liquid (2026-10-06).
 * Loaded only when the section's Look is "Sealy". Registers:
 *   MOLineupHeroes.buildup  the hero showpiece: a Queen Sealy that builds itself from its parts,
 *                           bottom up, each layer at the depth Sealy publishes, so the stack ends
 *                           at the real height. Four pickers beside it (Level, Model, Build, Feel)
 *                           re-stack the bed: the layers that change lift off, new ones drop in,
 *                           the rest stay. Press the bed and it gives; on the beds whose coil
 *                           unit Sealy describes with center support, it gives less across the
 *                           marked band (a drawing of the idea). Hover or tap a layer to name it.
 *   MOLineupDraw.sealy      the row drawings: a side view with the cover cut back.
 *   the ladder              the tier by build table: an entry opens its row on that build.
 * No libraries. Reduced motion: no build and no press, the finished stack only.
 *
 * Motion, after these 21st.dev components (ideas and constants, rebuilt in plain JS and WebGL):
 *   Toast Gravity Stack by nikolas-sapa (id 33302): fixed-step drop, a bounce only on a real hit,
 *     and a hard sleep so the stack ends at exact rest.
 *   Spring Element by skyleen77 (id 2923): the spring each layer's squash rides on (k 200, c 16
 *     for the coil unit, so it rings once).
 *   Squash Text by pacekit (id 19324): squash in proportion to the fall, a short contact, a small
 *     stretch on the way back.
 *   Kinetic Matrix by daiwiikharihar (id 24572): a spring per point with its own stiffness, used
 *     for the press on the bed (one stiffness across the marked band, another outside it).
 *   Dot Grid by paper-design (id 17862, adapted from Paper Shaders, Apache-2.0): the coil field
 *     as a cell grid in the fragment shader, with a pointer falloff. */
(function () {
  'use strict';
  if (window.MOLineupSealy) return;
  window.MOLineupSealy = true;

  var still = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var saver = !!(navigator.connection && navigator.connection.saveData);
  var heroes = window.MOLineupHeroes = window.MOLineupHeroes || {};
  var draws = window.MOLineupDraw = window.MOLineupDraw || {};

  function all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function money(cents) { return '$' + Math.round(cents / 100).toLocaleString('en-US'); }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function mix(a, b, t) { return a + (b - a) * t; }
  function inches(v) { return String(Math.round(v * 100) / 100); }
  function setText(el, text) { if (el && el.textContent !== text) el.textContent = text; }

  /* The shared script reads data-draw when it boots, which is after this file runs (both are
     deferred, this one first). The section adds no Sealy class to its root, so mark it here. */
  function mark(scope) {
    all('[data-mo-sealy] [data-mo-lineup]', scope).forEach(function (r) { if (r.getAttribute('data-draw') !== 'sealy') r.setAttribute('data-draw', 'sealy'); });
  }
  mark(document);
  document.addEventListener('shopify:section:load', function (e) { mark(e.target); });

  /* ---------- the accent word, built in three slices, bottom first ---------- */
  function stackWord(root) {
    var word = root.querySelector('[data-mo-stack]');
    if (!word || still || word.classList.contains('is-built')) return;
    var text = word.textContent, base = document.createElement('span'), i, last = null;
    word.textContent = '';
    base.textContent = text;
    word.appendChild(base);
    /* the slices carry the word as generated content, so the heading's text holds it once */
    for (i = 0; i < 3; i++) {
      last = document.createElement('i');
      last.setAttribute('aria-hidden', 'true');
      last.setAttribute('data-w', text);
      last.style.setProperty('--i', i);
      word.appendChild(last);
    }
    word.classList.add('is-built');
    last.addEventListener('animationend', function () { word.textContent = text; word.classList.remove('is-built'); });
  }

  /* colors: parsed by a detached 2D canvas, so any CSS color works and nothing touches the page */
  var probe = null;
  function rgb(value, fallback) {
    value = (value || '').trim();
    if (!value) return fallback;
    if (!probe) probe = document.createElement('canvas').getContext('2d');
    if (!probe) return fallback;
    probe.fillStyle = '#010203';
    probe.fillStyle = value;
    var s = probe.fillStyle, m;
    if (s === '#010203' && value.toLowerCase() !== '#010203') return fallback;
    if (s.charAt(0) === '#') return [parseInt(s.substr(1, 2), 16) / 255, parseInt(s.substr(3, 2), 16) / 255, parseInt(s.substr(5, 2), 16) / 255];
    if ((m = /rgba?\(([^)]+)\)/.exec(s))) { var p = m[1].split(',').map(parseFloat); return [p[0] / 255, p[1] / 255, p[2] / 255]; }
    return fallback;
  }

  /* ---------- small math ---------- */
  function sub(a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; }
  function dot(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
  function cross(a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
  function norm(a) { var l = Math.sqrt(dot(a, a)) || 1; return [a[0] / l, a[1] / l, a[2] / l]; }
  function lookAt(eye, at, up) {
    var f = norm(sub(at, eye)), s = norm(cross(f, up)), u = cross(s, f);
    return [s[0], u[0], -f[0], 0, s[1], u[1], -f[1], 0, s[2], u[2], -f[2], 0, -dot(s, eye), -dot(u, eye), dot(f, eye), 1];
  }
  function persp(fovy, aspect, near, far) {
    var f = 1 / Math.tan(fovy / 2), nf = 1 / (near - far);
    return [f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0];
  }
  function mul(a, b) {
    var o = new Array(16), i, j;
    for (i = 0; i < 4; i++) for (j = 0; j < 4; j++) o[j * 4 + i] = a[i] * b[j * 4] + a[4 + i] * b[j * 4 + 1] + a[8 + i] * b[j * 4 + 2] + a[12 + i] * b[j * 4 + 3];
    return o;
  }
  function xform(m, p) {
    var x = p[0], y = p[1], z = p[2];
    var w = m[3] * x + m[7] * y + m[11] * z + m[15];
    return [(m[0] * x + m[4] * y + m[8] * z + m[12]) / w, (m[1] * x + m[5] * y + m[9] * z + m[13]) / w, w];
  }
  function invert(m) {
    var inv = new Array(16), det, i;
    inv[0] = m[5] * m[10] * m[15] - m[5] * m[11] * m[14] - m[9] * m[6] * m[15] + m[9] * m[7] * m[14] + m[13] * m[6] * m[11] - m[13] * m[7] * m[10];
    inv[4] = -m[4] * m[10] * m[15] + m[4] * m[11] * m[14] + m[8] * m[6] * m[15] - m[8] * m[7] * m[14] - m[12] * m[6] * m[11] + m[12] * m[7] * m[10];
    inv[8] = m[4] * m[9] * m[15] - m[4] * m[11] * m[13] - m[8] * m[5] * m[15] + m[8] * m[7] * m[13] + m[12] * m[5] * m[11] - m[12] * m[7] * m[9];
    inv[12] = -m[4] * m[9] * m[14] + m[4] * m[10] * m[13] + m[8] * m[5] * m[14] - m[8] * m[6] * m[13] - m[12] * m[5] * m[10] + m[12] * m[6] * m[9];
    inv[1] = -m[1] * m[10] * m[15] + m[1] * m[11] * m[14] + m[9] * m[2] * m[15] - m[9] * m[3] * m[14] - m[13] * m[2] * m[11] + m[13] * m[3] * m[10];
    inv[5] = m[0] * m[10] * m[15] - m[0] * m[11] * m[14] - m[8] * m[2] * m[15] + m[8] * m[3] * m[14] + m[12] * m[2] * m[11] - m[12] * m[3] * m[10];
    inv[9] = -m[0] * m[9] * m[15] + m[0] * m[11] * m[13] + m[8] * m[1] * m[15] - m[8] * m[3] * m[13] - m[12] * m[1] * m[11] + m[12] * m[3] * m[9];
    inv[13] = m[0] * m[9] * m[14] - m[0] * m[10] * m[13] - m[8] * m[1] * m[14] + m[8] * m[2] * m[13] + m[12] * m[1] * m[10] - m[12] * m[2] * m[9];
    inv[2] = m[1] * m[6] * m[15] - m[1] * m[7] * m[14] - m[5] * m[2] * m[15] + m[5] * m[3] * m[14] + m[13] * m[2] * m[7] - m[13] * m[3] * m[6];
    inv[6] = -m[0] * m[6] * m[15] + m[0] * m[7] * m[14] + m[4] * m[2] * m[15] - m[4] * m[3] * m[14] - m[12] * m[2] * m[7] + m[12] * m[3] * m[6];
    inv[10] = m[0] * m[5] * m[15] - m[0] * m[7] * m[13] - m[4] * m[1] * m[15] + m[4] * m[3] * m[13] + m[12] * m[1] * m[7] - m[12] * m[3] * m[5];
    inv[14] = -m[0] * m[5] * m[14] + m[0] * m[6] * m[13] + m[4] * m[1] * m[14] - m[4] * m[2] * m[13] - m[12] * m[1] * m[6] + m[12] * m[2] * m[5];
    inv[3] = -m[1] * m[6] * m[11] + m[1] * m[7] * m[10] + m[5] * m[2] * m[11] - m[5] * m[3] * m[10] - m[9] * m[2] * m[7] + m[9] * m[3] * m[6];
    inv[7] = m[0] * m[6] * m[11] - m[0] * m[7] * m[10] - m[4] * m[2] * m[11] + m[4] * m[3] * m[10] + m[8] * m[2] * m[7] - m[8] * m[3] * m[6];
    inv[11] = -m[0] * m[5] * m[11] + m[0] * m[7] * m[9] + m[4] * m[1] * m[11] - m[4] * m[3] * m[9] - m[8] * m[1] * m[7] + m[8] * m[3] * m[5];
    inv[15] = m[0] * m[5] * m[10] - m[0] * m[6] * m[9] - m[4] * m[1] * m[10] + m[4] * m[2] * m[9] + m[8] * m[1] * m[6] - m[8] * m[2] * m[5];
    det = m[0] * inv[0] + m[1] * inv[4] + m[2] * inv[8] + m[3] * inv[12];
    if (!det) return null;
    for (i = 0; i < 16; i++) inv[i] /= det;
    return inv;
  }

  /* ==================================================================================
   * What is on the page: the model rows, their versions, sizes and layers
   * ================================================================================== */
  function readCatalog(root) {
    return all('[data-row][data-series]', root).map(function (el) {
      var versions = all('.mo-lineup__v', el).map(function (v, i) {
        var sizes = {};
        try { sizes = JSON.parse(v.querySelector('[data-sizes-json]').textContent); } catch (e) { /* no sizes */ }
        var layers = all('.mo-lineup__layers li', v).map(function (li) {
          var bits = (li.getAttribute('data-kind') || '').trim().split(/\s+/), text = (li.textContent || '').trim();
          var m = /(\d+(?:\.\d+)?)\s*in\./i.exec(text), c = /([\d,]{3,5})\s[^,]*coils/i.exec(text);
          return { kind: bits[0], top: bits.indexOf('top') > 0, inch: m ? parseFloat(m[1]) : 0, name: text, coils: c ? parseInt(c[1].replace(/,/g, ''), 10) : 0 };
        }).filter(function (l) { return l.kind; });
        var text = v.querySelector('.mo-lineup__build-text');
        var note = text ? text.textContent.replace(/^\s*[\d.]+\s*in\.\s*tall\s*/i, '').trim() : '';
        return { el: v, i: i, key: v.getAttribute('data-key'), build: v.getAttribute('data-build'), buildName: v.getAttribute('data-build-name') || '',
          feel: v.getAttribute('data-feel'), feelName: v.getAttribute('data-feel-name') || '', h: parseFloat(v.getAttribute('data-h')) || 0,
          url: v.getAttribute('data-url'), sizes: sizes, layers: layers, note: note };
      });
      return { el: el, id: el.id, tier: (el.getAttribute('data-tier') || '').replace(/[\u00ae\u2122]/g, '').trim(), model: el.getAttribute('data-model') || '', name: el.getAttribute('data-name') || '', versions: versions };
    }).filter(function (r) { return r.versions.length && r.id; });
  }

  /* A version's layers as bodies to stack, bottom up, in inches. Depths Sealy publishes are used as
     they are; a layer with none (the cover, sometimes the coils) takes what is left of the height;
     when the published depths add up to more than the finished height, all are scaled to fit. The
     cover and any layers Sealy puts in the pillow top land together, as the top. */
  var SKIN = 0.25;
  function isCover(k) { return k === 'cover' || k === 'chill'; }
  function isCoil(k) { return k.indexOf('coil') === 0; }
  function depths(layers, H) {
    var list = layers.map(function (l) { return { kind: l.kind, top: l.top, inch: l.inch, name: l.name, coils: l.coils }; });
    var known = 0, open = [], covers = [];
    list.forEach(function (l) {
      if (l.inch) l.t = l.inch; else { l.t = 0; (isCover(l.kind) ? covers : open).push(l); }
      known += l.t;
    });
    /* the cover has no depth of its own. It is what the published depths leave of the height, or
       a thin skin, in which case every published depth is scaled down by the same hair to fit */
    var left = H - known;
    covers.forEach(function (c) {
      if (!open.length && left >= SKIN) { c.t = Math.min(left, 1.25); left -= c.t; }
      else { c.t = SKIN; left -= SKIN; }
    });
    /* a layer with no published depth shares what is left of the height: the coil unit or foam core
       takes eight parts, a base one, a foam one and a quarter */
    if (open.length) {
      var weight = 0;
      left = Math.max(left, open.length * 0.8);
      open.forEach(function (l) { l.w = isCoil(l.kind) || l.kind === 'core' ? 8 : l.kind === 'base' ? 1 : 1.25; weight += l.w; });
      open.forEach(function (l) { l.t = left * l.w / weight; });
    }
    known = 0; list.forEach(function (l) { known += l.t; });
    /* published depths that add up to more than the finished height (quilting compresses) are scaled to fit */
    var k = H / known;
    if (Math.abs(k - 1) > 0.004) list.forEach(function (l) { l.t *= k; });
    return list;
  }
  function stackOf(version) {
    var H = version.h || 13, list = depths(version.layers, H);
    var bodies = [], top = { slabs: [], isTop: true }, i, l, inTop = true;
    for (i = 0; i < list.length; i++) {
      l = list[i];
      if (inTop && (isCover(l.kind) || l.top)) { top.slabs.unshift(l); continue; }
      inTop = false;
      bodies.unshift({ slabs: [l], isTop: false });
    }
    if (top.slabs.length) bodies.push(top);
    var zone = 0, pitch = 2.2;
    bodies.forEach(function (b) {
      b.t = 0;
      b.slabs.forEach(function (s) {
        s.off = b.t; b.t += s.t;
        if (s.kind === 'coil-third') zone = 1;
        if (s.kind === 'coil-max') zone = 2;
        if (isCoil(s.kind) && s.coils > 300) pitch = clamp(Math.sqrt(4800 / s.coils), 1.6, 2.8);
      });
      var main = b.slabs[0].kind;
      b.cls = b.isTop ? 'top' : isCoil(main) ? 'coil' : main === 'base' ? 'base' : main === 'mem' || main === 'gelmem' ? 'mem' : 'foam';
      b.key = b.slabs.map(function (s) { return s.kind + (s.top ? '^' : '') + ':' + s.name + ':' + s.t.toFixed(2); }).join('|');
    });
    return { h: H, bodies: bodies, zone: zone, firm: zone > 0, pitch: pitch, pillow: version.build === 'europillow' || version.build === 'pillow' };
  }

  /* ==================================================================================
   * The scene, in inches. A Queen is 60 in. across the foot (x) and 80 in. long (z, foot at 0,
   * head at -80). One mesh, a slab with rounded corners (a top face and a side strip), is drawn
   * once per layer with its own height, thickness and material.
   * ================================================================================== */
  var HX = 30, HZ = 40, RAD = 3.2, PERIM = 280;
  function slabMesh() {
    function lines(half) {
      var a = [], e = [0, 0.8, 1.6, 2.4, 3.2], n = Math.round((2 * half - 6.4) / 2), i;
      for (i = 0; i < e.length; i++) a.push(-half + e[i]);
      for (i = 1; i < n; i++) a.push(-half + 3.2 + (2 * half - 6.4) * i / n);
      for (i = e.length - 1; i >= 0; i--) a.push(half - e[i]);
      return a;
    }
    function warp(x, zc) {
      var ax = Math.abs(x) - (HX - RAD), az = Math.abs(zc) - (HZ - RAD);
      if (ax > 0 && az > 0) {
        var a = ax / RAD, b = az / RAD, a2 = a * Math.sqrt(1 - b * b / 2), b2 = b * Math.sqrt(1 - a * a / 2);
        x = (x < 0 ? -1 : 1) * (HX - RAD + a2 * RAD); zc = (zc < 0 ? -1 : 1) * (HZ - RAD + b2 * RAD);
      }
      return [x, zc];
    }
    var xs = lines(HX), zs = lines(HZ).reverse(), NX = xs.length, NZ = zs.length, v = [], top = [], side = [], i, j;
    /* zs runs from the foot (z = 0) to the head (z = -80) */
    function plan(i, j) { var w = warp(xs[i], zs[j]); return [w[0], w[1] - HZ]; }
    for (j = 0; j < NZ; j++) for (i = 0; i < NX; i++) { var p = plan(i, j); v.push(p[0], p[1], 1, 0, 1, 0, 0); }
    for (j = 0; j < NZ - 1; j++) for (i = 0; i < NX - 1; i++) { var a = j * NX + i; top.push(a, a + NX, a + 1, a + 1, a + NX, a + NX + 1); }
    var loop = [];
    for (j = 0; j < NZ; j++) loop.push([0, j]);
    for (i = 1; i < NX; i++) loop.push([i, NZ - 1]);
    for (j = NZ - 2; j >= 0; j--) loop.push([NX - 1, j]);
    for (i = NX - 2; i >= 1; i--) loop.push([i, 0]);
    var base = v.length / 7, u = 0, prev = null;
    loop.concat([loop[0]]).forEach(function (ij) {
      var p = plan(ij[0], ij[1]), zc = p[1] + HZ;
      var cx = clamp(p[0], -(HX - RAD), HX - RAD), cz = clamp(zc, -(HZ - RAD), HZ - RAD), n = norm([p[0] - cx, 0, zc - cz]);
      if (prev) u += Math.sqrt(Math.pow(p[0] - prev[0], 2) + Math.pow(p[1] - prev[1], 2));
      prev = p;
      v.push(p[0], p[1], 0, n[0], 0, n[2], u, p[0], p[1], 1, n[0], 0, n[2], u);
    });
    for (i = 0; i < loop.length; i++) { var q = base + i * 2; side.push(q, q + 1, q + 2, q + 2, q + 1, q + 3); }
    return { v: new Float32Array(v), top: new Uint16Array(top), side: new Uint16Array(side) };
  }
  /* a unit post, for the height mark beside the bed */
  function postMesh() {
    var v = [], top = [0, 1, 2, 2, 1, 3], side = [], c = [[-0.5, 0.5], [0.5, 0.5], [-0.5, -0.5], [0.5, -0.5]], i;
    c.forEach(function (p) { v.push(p[0], p[1], 1, 0, 1, 0, 0); });
    [[0, 1, 0, 0, 1], [1, 3, 1, 0, 0], [3, 2, 0, 0, -1], [2, 0, -1, 0, 0]].forEach(function (f) {
      var b = v.length / 7;
      [f[0], f[1]].forEach(function (k) { v.push(c[k][0], c[k][1], 0, f[2], f[3], f[4], 0, c[k][0], c[k][1], 1, f[2], f[3], f[4], 0); });
      side.push(b, b + 1, b + 2, b + 2, b + 1, b + 3);
    });
    return { v: new Float32Array(v), top: new Uint16Array(top), side: new Uint16Array(side) };
  }

  /* ---------- shaders ---------- */
  var HEAD = 'precision highp float;';
  var VS = [HEAD,
    'attribute vec3 aP; attribute vec3 aN; attribute float aU;',
    'uniform mat4 uVP; uniform vec3 uFit; uniform vec2 uFit2;',
    'uniform vec4 uBody; uniform vec4 uPlan; uniform vec4 uPress; uniform vec2 uShare; uniform float uZoneK;',
    'varying vec3 vP; varying vec3 vN; varying float vV; varying float vU; varying float vW;',
    'void main(){',
    '  vec2 pl = aP.xy * uPlan.xy + uPlan.zw;',
    '  vec2 q = pl - vec2(0.0, -40.0);',
    '  q *= vec2(1.0 + (uBody.w - uBody.z) / 30.0, 1.0 + (uBody.w - uBody.z) / 40.0);',
    '  pl = q + vec2(0.0, -40.0);',
    '  float y = uBody.x + aP.z * uBody.y;',
    '  vec2 d = pl - uPress.xy; float r2 = uPress.w * uPress.w;',
    '  float w = exp(-dot(d, d) / r2);',
    '  float pz = -uPress.y;',
    '  float zc = smoothstep(23.7, 29.7, pz) * (1.0 - smoothstep(50.3, 56.3, pz));',
    '  float depth = uPress.z * mix(1.0, uZoneK, zc);',
    '  y -= depth * w * mix(uShare.x, uShare.y, aP.z);',
    '  vec3 n = aN;',
    '  if (aN.y > 0.5) { vec2 g = (2.0 * depth * uShare.y * w / r2) * d; n = normalize(vec3(-g.x, 1.0, -g.y)); }',
    '  vP = vec3(pl.x, y, pl.y); vN = n; vV = aP.z; vU = aU; vW = w * depth;',
    '  vec4 c = uVP * vec4(vP, 1.0);',
    '  c.xy = (c.xy * uFit.x + uFit.yz * c.w) * uFit2;',
    '  gl_Position = c; }'].join('\n');

  /* materials: 0 quilted cover, 9 cool-to-the-touch cover, 1 comfort foam, 2 gel foam, 3 gel memory
     foam, 4 memory foam, 5 support foam, 6 base foam, 7 encased coils, 8 ink (the height mark),
     10 a center support layer (gel memory foam through the center third, plain foam at the ends) */
  var FS = [HEAD,
    'uniform vec3 uEye; uniform vec3 cKey; uniform vec3 cSky; uniform vec3 cGround; uniform float uExpo;',
    'uniform int uMat; uniform vec3 cA; uniform vec3 cB; uniform vec3 cC; uniform vec3 cBlue; uniform vec3 cCover;',
    'uniform vec4 uBody; uniform float uAlpha; uniform float uGap; uniform float uSeam; uniform vec2 uEdge;',
    'uniform float uZone; uniform float uFirm; uniform float uCoils; uniform float uPitch; uniform float uXray; uniform vec4 uPress; uniform float uHot; uniform vec2 uTex;',
    'uniform sampler2D tKnit; uniform sampler2D tFoam;',
    'varying vec3 vP; varying vec3 vN; varying float vV; varying float vU; varying float vW;',
    'const vec3 LKEY = vec3(0.26, 0.84, 0.47);',
    'vec3 tone(vec3 c){ c *= uExpo; c = (c*(2.51*c+0.03))/(c*(2.43*c+0.59)+0.14); return pow(clamp(c,0.0,1.0), vec3(1.0/2.2)); }',
    'float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }',
    'float noise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f);',
    '  return mix(mix(hash(i), hash(i+vec2(1,0)), f.x), mix(hash(i+vec2(0,1)), hash(i+vec2(1,1)), f.x), f.y); }',
    /* distance to the bed outline, seen from above: negative inside */
    'float sdBed(vec2 p){ vec2 q = abs(p - vec2(0.0, -40.0)) - vec2(30.0, 40.0) + 3.2; return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - 3.2; }',
    /* inches round the edge from the near left corner: up the left side, across the head, down the right, back along the foot */
    'float perim(vec2 q){ float l = q.x + 30.0, r = 30.0 - q.x, f = -q.y, h = 80.0 + q.y; float m = min(min(l, r), min(f, h));',
    '  if (m == l) return clamp(-q.y, 0.0, 80.0);',
    '  if (m == h) return 80.0 + clamp(q.x + 30.0, 0.0, 60.0);',
    '  if (m == r) return 140.0 + clamp(80.0 + q.y, 0.0, 80.0);',
    '  return 220.0 + clamp(30.0 - q.x, 0.0, 60.0); }',
    /* 1 inside the center third of the length (Sealy's chart: PrecisionFit Coils, Center Third) */
    'float third(float z){ return smoothstep(26.52, 26.82, -z) * (1.0 - smoothstep(53.18, 53.48, -z)); }',
    /* MaxSupport: Sealy says "in the center" and gives no width, so this band has soft ends and no line */
    'float band(float z){ return smoothstep(25.0, 31.5, -z) * (1.0 - smoothstep(48.5, 55.0, -z)); }',
    'vec3 shade(vec3 base, vec3 p, vec3 n, float rough, float specK, float sheen){',
    '  vec3 v = normalize(uEye - p); vec3 L = normalize(LKEY);',
    '  float dif = max(dot(n, L), 0.0);',
    '  vec3 amb = mix(cGround, cSky, n.y*0.5+0.5);',
    '  vec3 h = normalize(L + v); float sp = pow(max(dot(n, h), 0.0), mix(8.0, 140.0, 1.0-rough)) * specK;',
    '  float rim = pow(1.0 - max(dot(n, v), 0.0), 3.0) * sheen;',
    '  float fill = max(dot(n, normalize(vec3(-0.75, 0.3, 0.45))), 0.0) * 0.36;',
    '  return base * (amb*0.55 + cKey*(dif + fill)) + cKey*sp + mix(base, vec3(1.0), 0.35)*rim*0.22; }',
    'void main(){',
    '  vec3 n = normalize(vN); vec3 p = vP; bool top = n.y > 0.6;',
    '  float rough = 0.92, spk = 0.0, sheen = 0.0; vec3 col;',
    '  bool lng = abs(n.x) > abs(n.z);',
    '  float along = lng ? p.z : p.x;',
    '  float t = uBody.y, hy = vV * t;',
    '  float bx = -sdBed(p.xz);',
    '  if (uMat == 8) { gl_FragColor = vec4(pow(cA, vec3(1.0/2.2)) * uAlpha, uAlpha); return; }',
    '  if (uMat == 0 || uMat == 9) {',
    '    float cool = uMat == 9 ? 1.0 : 0.0;',
    '    if (top) {',
    /* the top panel: knit, quilted in a diamond lattice, a flat tape band round the edge */
    '      vec2 q = p.xz;',
    '      float knit = uTex.x > 0.5 ? texture2D(tKnit, q / 9.0).r * 1.25 - 0.1 : noise(q*vec2(9.0, 3.0))*0.5 + noise(q*vec2(23.0, 31.0))*0.5;',
    '      float s = 0.7071 / mix(5.4, 4.2, cool);',
    '      vec2 f = fract(vec2(q.x + q.y, q.x - q.y) * s) - 0.5;',
    '      float band = smoothstep(2.9, 3.4, bx);',
    '      float puff = cos(3.14159*f.x) * cos(3.14159*f.y);',
    '      float gx = -3.14159 * sin(3.14159*f.x) * cos(3.14159*f.y), gy = -3.14159 * cos(3.14159*f.x) * sin(3.14159*f.y);',
    '      vec2 grad = vec2(gx + gy, gx - gy) * s * mix(0.62, 0.3, cool) * band;',
    '      n = normalize(n + vec3(-grad.x, 0.0, -grad.y));',
    '      float stitch = 1.0 - smoothstep(0.0, 0.03, min(0.5 - abs(f.x), 0.5 - abs(f.y)));',
    '      col = cA * (0.9 + 0.12*knit) * (1.0 - 0.24*stitch*band) * (0.9 + 0.1*puff*band + 0.1*(1.0 - band));',
    '      col *= mix(0.84, 1.0, smoothstep(0.0, 1.3, bx));',
    /* the tape edge: one stitched line of Sealy blue, sewn round as the seam closes */
    '      float pm = perim(q);',
    '      float sewn = step(pm / 280.0, uSeam);',
    '      float line = (1.0 - smoothstep(0.03, 0.09, abs(bx - 2.75))) * step(0.42, fract(pm / 0.62));',
    '      col = mix(col, cBlue, line * sewn * 0.92);',
    '      col *= 1.0 - 0.1 * (1.0 - smoothstep(0.0, 0.1, abs(bx - 3.05))) * sewn;',
    '      rough = mix(0.82, 0.6, cool); sheen = mix(0.5, 0.9, cool); spk = mix(0.04, 0.12, cool);',
    '    } else {',
    '      float rib = 0.5 + 0.5*sin(along * 7.5);',
    '      float knit = noise(vec2(along*11.0, p.y*17.0));',
    '      col = cA * (0.9 + 0.06*rib + 0.06*knit); rough = 0.85; sheen = 0.4;',
    '    }',
    '  } else if (uMat == 7) {',
    '    float zc = uZone > 1.5 ? band(p.z) : third(p.z);',
    '    if (top) {',
    /* the coil unit from above: the tops of the pockets, at one spacing from head to foot. Where
       Sealy names the coils of the center third they take the blue; where Sealy adds an extra set
       of coils in the center, smaller blue coils sit between the main ones. */
    '      vec2 q = p.xz;',
    '      vec2 f = fract(q / uPitch) - 0.5; float r = length(f);',
    '      float disc = 1.0 - smoothstep(0.38, 0.46, r);',
    '      float dome = sqrt(max(0.0, 1.0 - r*r*4.4));',
    '      col = cA * mix(0.6, 0.86 + 0.16*dome, disc);',
    '      n = normalize(vec3(-f.x*1.3*disc, 1.0, -f.y*1.3*disc));',
    '      if (uZone > 0.5 && uZone < 1.5) col = mix(col, cBlue, 0.26 * zc * disc);',
    '      if (uZone > 1.5) {',
    '        vec2 f2 = fract(q / uPitch + 0.5) - 0.5; float r2 = length(f2);',
    '        col = mix(col, mix(cA, cBlue, 0.6) * (0.9 + 0.1 * sqrt(max(0.0, 1.0 - r2*r2*14.0))), (1.0 - smoothstep(0.19, 0.25, r2)) * zc);',
    '      }',
    '      col = mix(cA * 0.78, col, smoothstep(0.7, 1.2, bx));',
    '    } else {',
    /* the coil unit from the side: a row of fabric pockets, the wire a faint shadow inside */
    '      float inz = lng ? zc : 0.0;',
    '      float fx = fract(along / uPitch) - 0.5;',
    '      float cyl = sqrt(max(0.0, 1.0 - 4.0*fx*fx));',
    '      vec3 tang = lng ? vec3(0.0, 0.0, 1.0) : vec3(1.0, 0.0, 0.0);',
    '      n = normalize(n * (0.4 + cyl) + tang * fx * 1.6);',
    '      float fib = noise(vec2(along*13.0, p.y*5.0))*0.5 + noise(vec2(along*31.0, p.y*27.0))*0.5;',
    '      col = cA * (0.88 + 0.12*fib);',
    '      float wire = sin(vV * 6.5 * 6.28318 + fx * 3.2);',
    '      col *= 1.0 - 0.09 * smoothstep(0.5, 0.95, wire) * cyl;',
    '      col *= mix(0.7, 1.0, smoothstep(0.0, 0.25, cyl));',
    '      if (uZone > 0.5 && uZone < 1.5 && inz > 0.5) col = mix(col, cBlue, 0.2);',
    /* the extra set of coils: a narrow blue pocket between each pair of main pockets */
    '      if (uZone > 1.5) col = mix(col, mix(cA, cBlue, 0.66) * (0.86 + 0.14*fib), smoothstep(0.31, 0.4, abs(fx)) * inz * 0.9);',
    '      if (uZone > 0.5 && uZone < 1.5 && lng) {',
    '        float zl = min(abs(-p.z - 26.67), abs(-p.z - 53.33));',
    '        col = mix(col, cBlue, (1.0 - smoothstep(0.04, 0.12, zl)) * step(0.45, fract(hy / 0.55)) * 0.9);',
    '      }',
    '      rough = 0.95; sheen = 0.12;',
    '    }',
    '  } else {',
    /* foams: a base color, an open-cell grain, and beads of gel in the gel foams */
    '    vec2 q = top ? p.xz : vec2(along, p.y);',
    '    float grain = noise(q*7.0)*0.5 + noise(q*19.0)*0.35 + noise(q*41.0)*0.15;',
    '    if (uTex.y > 0.5) grain = mix(grain, texture2D(tFoam, q / 4.5).r, 0.7);',
    '    float pores = uMat == 6 ? 1.0 : uMat == 5 ? 0.8 : uMat == 4 ? 0.25 : 0.55;',
    '    float cell = smoothstep(0.62, 0.8, noise(q*26.0 + 3.1)) * pores;',
    '    float mid = (top || lng) ? third(p.z) : 0.0;',
    '    vec3 tintc = uMat == 10 ? mix(cA, cC, mid) : cA;',
    '    col = tintc * (0.9 + 0.16*grain) * (1.0 - cell*0.17);',
    '    if (uMat == 2 || uMat == 3 || (uMat == 10 && mid > 0.5)) {',
    '      vec2 g = q * (uMat == 3 ? 3.4 : 2.6); vec2 id = floor(g); vec2 f = fract(g) - 0.5 - (vec2(hash(id), hash(id + 7.3)) - 0.5) * 0.6;',
    '      float bead = (1.0 - smoothstep(0.1, 0.17, length(f))) * step(0.35, hash(id + 2.1));',
    '      col = mix(col, cB, bead * 0.7);',
    '    }',
    '  }',
    '  if (!top) {',
    /* where two layers meet, and the tape at the top and bottom of the finished bed */
    '    float e = min(hy, t - hy);',
    '    col *= mix(0.78, 1.0, smoothstep(0.0, 0.42, e));',
    '    float pm = perim(p.xz); float sewn = step(pm / 280.0, uSeam);',
    '    if (uEdge.x > 0.5) {',
    '      col = mix(col, cCover * 1.02, smoothstep(t - 0.34, t - 0.27, hy) * sewn);',
    '      col = mix(col, cBlue, (1.0 - smoothstep(0.02, 0.07, abs(hy - (t - 0.47)))) * step(0.42, fract(pm / 0.62)) * sewn * 0.92);',
    '    }',
    '    if (uEdge.y > 0.5) {',
    '      col = mix(col, cCover * 0.96, (1.0 - smoothstep(0.27, 0.34, hy)) * sewn);',
    '      col = mix(col, cBlue, (1.0 - smoothstep(0.02, 0.07, abs(hy - 0.47))) * step(0.42, fract(pm / 0.62)) * sewn * 0.92);',
    '    }',
    '  }',
    '  if (top && uXray > 0.002) {',
    /* a press shows the coils under the hand, and the band where Sealy marks one */
    '    vec2 q = p.xz; float zc = uZone > 1.5 ? band(q.y) : third(q.y);',
    '    vec2 f = fract(q / uPitch) - 0.5;',
    '    vec2 dq = q - uPress.xy; float fall = exp(-dot(dq, dq) / (uPress.w * uPress.w * 2.4));',
    '    float ring = 1.0 - smoothstep(0.05, 0.13, abs(length(f) - mix(0.36, 0.2, clamp(vW / 2.6, 0.0, 1.0))));',
    '    vec3 xc = mix(cA * 0.32, cBlue, (uFirm > 0.5 && uZone < 1.5) ? zc : 0.0);',
    '    col = mix(col, xc, ring * fall * uXray * 0.9 * uCoils);',
    '    if (uZone > 1.5) {',
    '      vec2 f2 = fract(q / uPitch + 0.5) - 0.5;',
    '      float ring2 = 1.0 - smoothstep(0.04, 0.1, abs(length(f2) - 0.17));',
    '      col = mix(col, cBlue, ring2 * zc * fall * uXray * 0.9 * uCoils);',
    '    }',
    '    if (uFirm > 0.5) {',
    '      float inb = smoothstep(1.5, 3.2, bx);',
    '      col = mix(col, cBlue, zc * 0.07 * uXray * inb);',
    '      if (uZone < 1.5) {',
    '        float zl = min(abs(-q.y - 26.67), abs(-q.y - 53.33));',
    '        float line = (1.0 - smoothstep(0.06, 0.16, zl)) * step(0.45, fract(q.x / 0.9)) * inb;',
    '        col = mix(col, cBlue, line * uXray * 0.9);',
    '      }',
    '    }',
    /* the hollow under the hand is in shade */
    '    col *= 1.0 - 0.3 * smoothstep(0.0, 2.4, vW) * uXray;',
    '  }',
    '  col *= 1.0 + 0.1 * uHot;',
    '  vec3 c = shade(col, p, n, rough, spk, sheen);',
    '  if (top && uGap < 80.0) {',
    /* the shadow of the layer hovering above */
    '    vec2 o = p.xz + LKEY.xz / LKEY.y * uGap * 0.55;',
    '    float soft = 0.7 + uGap * 0.34;',
    '    float sh = 1.0 - smoothstep(-soft, soft, sdBed(o));',
    '    c *= 1.0 - sh * mix(0.5, 0.1, clamp(uGap / 46.0, 0.0, 1.0));',
    '  }',
    '  gl_FragColor = vec4(tone(c) * uAlpha, uAlpha); }'].join('\n');

  var VS_FLOOR = [HEAD,
    'attribute vec2 aXZ; uniform mat4 uVP; uniform vec3 uFit; uniform vec2 uFit2; varying vec2 vXZ;',
    'void main(){ vXZ = aXZ; vec4 c = uVP * vec4(aXZ.x, 0.0, aXZ.y, 1.0); c.xy = (c.xy * uFit.x + uFit.yz * c.w) * uFit2; gl_Position = c; }'].join('\n');
  var FS_FLOOR = [HEAD,
    'uniform vec3 cShadow; uniform float uShadow; uniform float uLift; varying vec2 vXZ;',
    'void main(){',
    '  vec2 q = abs(vXZ - vec2(-1.2 - uLift * 0.3, -40.0 - 1.4 - uLift * 0.5)) - vec2(30.0, 40.0) + 2.0;',
    '  float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - 2.0;',
    '  float a = (1.0 - smoothstep(-2.0, 7.0 + uLift * 0.9, d)) * 0.42 + (1.0 - smoothstep(-0.6, 1.6 + uLift * 0.7, d)) * 0.58;',
    '  a *= uShadow / (1.0 + uLift * 0.06);',
    '  gl_FragColor = vec4(cShadow * a, a); }'].join('\n');

  var MAT = { cover: 0, chill: 9, foam: 1, gel: 2, gelmem: 3, mem: 4, support: 5, core: 5, base: 6, coil: 7, 'coil-third': 7, 'coil-max': 7, center: 10 };
  var TINT = { cover: 'cover', chill: 'chill', foam: 'foam', gel: 'gel', gelmem: 'gelmem', mem: 'mem', support: 'support', core: 'support', base: 'base', coil: 'pocket', 'coil-third': 'pocket', 'coil-max': 'pocket', center: 'foam' };
  /* how much of a press each kind of layer takes up */
  var GIVE = { cover: 0.05, chill: 0.05, foam: 0.14, gel: 0.14, gelmem: 0.16, mem: 0.16, support: 0.08, center: 0.06, core: 0.3, base: 0, coil: 0.5, 'coil-third': 0.5, 'coil-max': 0.5 };
  /* how each kind of body lands: spring rate and damping of its squash, how much a hit squashes it,
     and how much it bounces (the coil unit rings once, memory foam comes back slowly) */
  var LAND = {
    coil: { k: 200, c: 16, kick: 0.0011, rest: 0.2 },
    base: { k: 2400, c: 90, kick: 0.0002, rest: 0 },
    foam: { k: 900, c: 44, kick: 0.0011, rest: 0.05 },
    mem: { k: 130, c: 34, kick: 0.0016, rest: 0 },
    top: { k: 340, c: 22, kick: 0.0015, rest: 0.07 }
  };
  var GRAV = 300, STEP = 1 / 240;

  heroes.buildup = function (root) {
    var hero = root.querySelector('[data-mo-lineup-hero]');
    var stage = hero && hero.querySelector('.mo-sl-stage');
    var canvas = stage && stage.querySelector('canvas');
    stackWord(root);
    if (!canvas) return function () {};

    /* only the beds with a layer list can be built: the drawing never invents a layer */
    var catalog = readCatalog(root).map(function (r) {
      r.versions = r.versions.filter(function (v) { return v.layers.length > 0; });
      return r;
    }).filter(function (r) { return r.versions.length; });
    var panel = hero.querySelector('[data-config]');
    var zoneOn = stage.getAttribute('data-zone') === 'true';
    var zoneNames = { 1: stage.getAttribute('data-zone-third') || '', 2: stage.getAttribute('data-zone-max') || '' }, zoneCall = false;
    var current = null;                     /* { row, v } */
    var tallest = 14;
    catalog.forEach(function (r) { r.versions.forEach(function (v) { if (v.h > tallest) tallest = v.h; }); });

    /* ---------- the bed's state: the bodies in the stack, the ones leaving, the press ---------- */
    var bodies = [], ghosts = [], model = null, now = 0, seam = 1, seamHold = 0, shown = -1;
    var press = { x: 0, z: -40, d: 0, v: 0, to: 0, on: false, xray: 0 };
    var hot = null, hotA = 0;

    function restTop(i) { var y = 0, k; for (k = 0; k < i; k++) y += bodies[k].t; return y; }
    function makeBody(spec, y, state, release) {
      var L = LAND[spec.cls];
      return { key: spec.key, slabs: spec.slabs, t: spec.t, cls: spec.cls, isTop: spec.isTop, y: y, vy: 0, s: 0, sv: 0, k: L.k, c: L.c, kick: L.kick, rest: L.rest,
        state: state, hold: y, release: release, born: release - 0.16, alpha: state === 'rest' ? 1 : 0, hits: 0, landed: 0, fresh: state !== 'rest' };
    }
    /* set the stack to a model. animate: the bodies above the first change lift (the ones that are
       in both hover, the rest leave) and the new ones drop in, bottom first. */
    function setStack(next, animate, gap) {
      model = next;
      var keep = 0, i, j, old = bodies, fresh = [], used = [];
      if (!animate) {
        bodies = next.bodies.map(function (spec) { return makeBody(spec, 0, 'rest', 0); });
        var y = 0;
        bodies.forEach(function (b) { b.y = y; y += b.t; });
        ghosts = []; seam = 1; seamHold = 0;
        return;
      }
      while (keep < old.length && keep < next.bodies.length && old[keep].key === next.bodies[keep].key && old[keep].state === 'rest') keep++;
      var t0 = now + (old.length > keep ? 0.2 : 0.04), y0 = 0, n = 0;
      for (i = 0; i < keep; i++) { fresh.push(old[i]); y0 += old[i].t; }
      for (i = keep; i < next.bodies.length; i++) {
        var spec = next.bodies[i], match = null;
        for (j = keep; j < old.length; j++) if (used.indexOf(j) < 0 && old[j].key === spec.key) { match = old[j]; used.push(j); break; }
        var release = t0 + n * gap + (spec.isTop && n > 0 ? gap * 0.6 : 0);
        if (match) {
          /* in both: hover above its place until the layers under it have landed */
          match.state = 'hold'; match.hold = Math.max(match.y, y0) + 7 + n * 2.5; match.release = release; match.hits = 0; match.vy = 0; match.fresh = false;
          fresh.push(match);
        } else {
          fresh.push(makeBody(spec, y0 + 30 + n * 5, 'hold', release));
        }
        y0 += spec.t; n++;
      }
      for (j = keep; j < old.length; j++) if (used.indexOf(j) < 0) { old[j].state = 'leave'; old[j].vy = 26; ghosts.push(old[j]); }
      bodies = fresh;
      seam = Math.min(seam, 0.0001); seamHold = 0;
    }

    function kick(i, speed) {
      var b = bodies[i], k;
      b.sv += speed * b.kick * 60;
      for (k = i - 1; k >= 0; k--) bodies[k].sv += speed * bodies[k].kick * 60 * 0.5 * Math.pow(0.62, i - 1 - k);
    }
    function step(dt) {
      now += dt;
      var floor = 0, i, b, settled = true;
      for (i = 0; i < bodies.length; i++) {
        b = bodies[i];
        if (b.state === 'hold') {
          b.y += (Math.max(b.hold, floor) - b.y) * (1 - Math.exp(-dt * 11)); b.vy = 0;
          if (b.fresh) b.alpha = clamp((now - b.born) / 0.16, 0, 1);
          if (now >= b.release) { b.state = 'fall'; b.alpha = 1; }
          settled = false;
        } else if (b.state === 'fall') {
          b.vy -= GRAV * dt; b.y += b.vy * dt;
          if (b.y <= floor) {
            var hit = -b.vy;
            b.y = floor; b.hits++;
            if (!b.landed) b.landed = now;
            kick(i, Math.min(hit, 150));
            /* a bounce only on a real first hit; after that the body stays down */
            if (hit > 45 && b.hits < 2 && b.rest > 0) b.vy = hit * b.rest; else { b.vy = 0; b.state = 'rest'; }
          }
          settled = false;
        } else {
          b.y = floor; b.vy = 0;
        }
        b.sv += (-b.k * b.s - b.c * b.sv) * dt; b.s = clamp(b.s + b.sv * dt, -0.12, 0.42);
        /* a hard stop, so the stack always ends at the published height */
        if (b.state === 'rest' && b.landed && now - b.landed > 2.4) { b.s = 0; b.sv = 0; }
        if (Math.abs(b.s) > 0.0006 || Math.abs(b.sv) > 0.004) settled = false;
        floor = b.y + b.t * (1 - b.s);
      }
      for (i = ghosts.length - 1; i >= 0; i--) {
        b = ghosts[i];
        b.vy += 220 * dt; b.y += b.vy * dt; b.alpha -= dt / 0.26;
        if (b.alpha <= 0) ghosts.splice(i, 1);
        settled = false;
      }
      /* the seam: open while anything moves, sewn round once the top has been down a moment */
      var down = bodies.length > 0 && !ghosts.length && bodies.every(function (x) { return x.state === 'rest'; });
      if (down) { seamHold += dt; if (seamHold > 0.12 && seam < 1) { seam = Math.min(1, seam + dt / 0.85); settled = false; } }
      else { seamHold = 0; }
      /* the press: a spring toward the hand. Across a marked band it gives less, by one fixed
         amount on every bed that has a band (a drawing of the idea, not a measurement) */
      var zk = zoneK(press.z), pk = 520 / zk, pc = 34;
      press.v += ((press.to - press.d) * pk - press.v * pc) * dt; press.d = clamp(press.d + press.v * dt, -0.25, 4);
      press.xray += ((press.on ? 1 : 0) - press.xray) * (1 - Math.exp(-dt * (press.on ? 9 : 4)));
      if (Math.abs(press.d - press.to) > 0.004 || Math.abs(press.v) > 0.02 || (press.xray > 0.004 && !press.on)) settled = false;
      hotA += ((hot ? 1 : 0) - hotA) * (1 - Math.exp(-dt * 12));
      if (Math.abs(hotA - (hot ? 1 : 0)) > 0.01) settled = false;
      return settled;
    }
    function zoneK(z) {
      if (!model || !model.firm || !zoneOn) return 1;
      var a = clamp((-z - 23.7) / 6, 0, 1), b = clamp((-z - 50.3) / 6, 0, 1);
      a = a * a * (3 - 2 * a); b = b * b * (3 - 2 * b);
      return mix(1, 0.56, a * (1 - b));
    }
    function stackTop() { var b = bodies[bodies.length - 1]; return b ? b.y + b.t * (1 - b.s) : 0; }
    function landedHeight() {
      var y = 0, i;
      for (i = 0; i < bodies.length; i++) { if (bodies[i].state !== 'rest') break; y += bodies[i].t; }
      return y;
    }

    /* ---------- WebGL ---------- */
    var gl = null, progs = {}, bufs = {}, tex = { knit: null, foam: null, has: [0, 0] }, lost = false;
    function context() {
      try {
        gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: true, preserveDrawingBuffer: !!window.__moSlKeep }) || canvas.getContext('experimental-webgl');
      } catch (e) { gl = null; }
      return !!gl;
    }
    function shader(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    }
    function program(vs, fs) {
      var p = gl.createProgram();
      gl.attachShader(p, shader(gl.VERTEX_SHADER, vs)); gl.attachShader(p, shader(gl.FRAGMENT_SHADER, fs));
      gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
      var info = { p: p, a: {}, u: {} }, n, i;
      n = gl.getProgramParameter(p, gl.ACTIVE_ATTRIBUTES);
      for (i = 0; i < n; i++) { var a = gl.getActiveAttrib(p, i); info.a[a.name] = gl.getAttribLocation(p, a.name); }
      n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
      for (i = 0; i < n; i++) { var u = gl.getActiveUniform(p, i); info.u[u.name.replace(/\[0\]$/, '')] = gl.getUniformLocation(p, u.name); }
      return info;
    }
    function meshBuffers(m) {
      var o = { v: gl.createBuffer(), top: gl.createBuffer(), side: gl.createBuffer(), nTop: m.top.length, nSide: m.side.length };
      gl.bindBuffer(gl.ARRAY_BUFFER, o.v); gl.bufferData(gl.ARRAY_BUFFER, m.v, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, o.top); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, m.top, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, o.side); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, m.side, gl.STATIC_DRAW);
      return o;
    }
    function white() {
      var t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([128, 128, 128, 255]));
      return t;
    }
    function setup() {
      try {
        progs.slab = program(VS, FS);
        progs.floor = program(VS_FLOOR, FS_FLOOR);
      } catch (e) { if (window.__moSlDebug) console.error(e); return false; }
      bufs.slab = meshBuffers(slabMesh());
      bufs.post = meshBuffers(postMesh());
      bufs.floor = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, bufs.floor);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-90, 60, 90, 60, -90, -150, 90, -150]), gl.STATIC_DRAW);
      tex.knit = white(); tex.foam = white(); tex.has = [0, 0];
      return true;
    }
    /* the material photographs (512 px, tiling): detail for the knit and the foam cells */
    function loadTexture(name, slot) {
      var url = canvas.getAttribute('data-tex-' + name);
      if (!url || saver || !gl) return;
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        if (lost || !gl) return;
        var pot = img.width === img.height && (img.width & (img.width - 1)) === 0;
        if (!pot) return;
        var t = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, t);
        try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img); } catch (e) { return; }
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        tex[name] = t; tex.has[slot] = 1;
        wake();
      };
      img.onerror = function () { /* the drawn grain stays */ };
      img.src = url;
    }

    /* ---------- colors ---------- */
    var COL = {};
    function readColors() {
      var css = getComputedStyle(stage);
      /* CSS colors are sRGB; the shader lights in linear space */
      function c(name, fb) { return rgb(css.getPropertyValue(name), fb).map(function (v) { return Math.pow(v, 2.2); }); }
      COL.cover = c('--sl-cover', [0.94, 0.93, 0.9]); COL.chill = c('--sl-chill', [0.9, 0.92, 0.93]);
      COL.foam = c('--sl-foam', [0.93, 0.89, 0.78]); COL.gel = c('--sl-gel', [0.79, 0.87, 0.93]); COL.gelmem = c('--sl-gelmem', [0.66, 0.78, 0.89]);
      COL.mem = c('--sl-mem', [0.91, 0.84, 0.67]); COL.support = c('--sl-support', [0.86, 0.84, 0.78]); COL.base = c('--sl-base', [0.74, 0.71, 0.66]);
      COL.pocket = c('--sl-pocket', [0.95, 0.94, 0.92]); COL.blue = c('--sl-blue', [0, 0.3, 0.66]); COL.ink = c('--sl-ink', [0.1, 0.11, 0.11]);
      COL.bead = c('--sl-blue', [0, 0.3, 0.66]).map(function (v, i) { return mix(v, COL.gel[i], 0.45); });
      COL.key = c('--sl-key', [1, 0.96, 0.9]); COL.sky = c('--sl-sky', [0.72, 0.72, 0.7]); COL.ground = c('--sl-ground', [0.43, 0.4, 0.36]);
      COL.shadow = c('--sl-shadow-color', [0.22, 0.18, 0.15]);
      COL.shadowA = parseFloat(css.getPropertyValue('--sl-shadow')) || 0.3;
      COL.expo = parseFloat(css.getPropertyValue('--sl-expo')) || 1.05;
    }

    /* ---------- camera, fitted to the tallest bed on the page, so every bed shares one floor line
       and one scale ---------- */
    var W = 0, Hc = 0, dpr = 1, VP = null, IVP = null, FIT = [1, 0, 0], FIT2 = [1, 1], EYE = null, PA = 1.5;
    function camera() {
      var o = window.__moSlCam || {};
      var target = o.target || [0, 6, -40];
      var dir = norm(o.dir || [-0.5, 0.42, 0.76]);
      var dist = o.dist || 270;
      var eye = [target[0] + dir[0] * dist, target[1] + dir[1] * dist, target[2] + dir[2] * dist];
      var vp = mul(persp(o.fov || 0.3, PA, 30, 900), lookAt(eye, target, [0, 1, 0]));
      var x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9, top = tallest + (o.air != null ? o.air : 5);
      [-HX, HX].forEach(function (x) { [0, -2 * HZ].forEach(function (z) { [0, top].forEach(function (y) {
        var q = xform(vp, [x, y, z]); x0 = Math.min(x0, q[0]); x1 = Math.max(x1, q[0]); y0 = Math.min(y0, q[1]); y1 = Math.max(y1, q[1]);
      }); }); });
      var mL = o.mL != null ? o.mL : 0.12, mR = o.mR != null ? o.mR : 0.04, mT = o.mT != null ? o.mT : 0.03, mB = o.mB != null ? o.mB : 0.15;
      var s = Math.min((2 - mL - mR) / (x1 - x0), (2 - mT - mB) / (y1 - y0));
      /* sit the bed on the bottom margin; center it between the side margins */
      FIT = [s, -(x0 + x1) / 2 * s + (mL - mR) / 2, -1 + mB - y0 * s];
      /* that frame (aspect PA) inside the canvas, the way object-fit: contain places the still */
      var ca = W / Hc;
      FIT2 = ca > PA ? [PA / ca, 1] : [1, ca / PA];
      VP = vp; EYE = eye; IVP = invert(vp);
    }
    function resize() {
      var r = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = Math.max(2, Math.round(r.width * dpr)); Hc = Math.max(2, Math.round(r.height * dpr));
      if (canvas.width !== W || canvas.height !== Hc) { canvas.width = W; canvas.height = Hc; }
      camera();
    }
    function unproject(x, y, z) {
      var m = IVP, w = m[3] * x + m[7] * y + m[11] * z + m[15];
      return [(m[0] * x + m[4] * y + m[8] * z + m[12]) / w, (m[1] * x + m[5] * y + m[9] * z + m[13]) / w, (m[2] * x + m[6] * y + m[10] * z + m[14]) / w];
    }
    function ray(clientX, clientY) {
      var r = canvas.getBoundingClientRect();
      var nx = ((clientX - r.left) / r.width) * 2 - 1, ny = 1 - ((clientY - r.top) / r.height) * 2;
      nx = (nx / FIT2[0] - FIT[1]) / FIT[0]; ny = (ny / FIT2[1] - FIT[2]) / FIT[0];
      var a = unproject(nx, ny, -1), b = unproject(nx, ny, 1);
      return { o: a, d: sub(b, a) };
    }
    /* a screen point on the bed: its top, or one of the two faces toward the viewer */
    function pick(clientX, clientY) {
      if (!IVP || !bodies.length) return null;
      var R = ray(clientX, clientY), top = stackTop(), t, p, best = null;
      if (Math.abs(R.d[1]) > 1e-6) {
        t = (top - R.o[1]) / R.d[1];
        p = [R.o[0] + R.d[0] * t, top, R.o[2] + R.d[2] * t];
        if (p[0] > -HX && p[0] < HX && p[2] < 0 && p[2] > -2 * HZ) best = { face: 'top', x: p[0], z: p[2], t: t };
      }
      [[0, -HX], [2, 0]].forEach(function (f) {
        if (Math.abs(R.d[f[0]]) < 1e-6) return;
        var tt = (f[1] - R.o[f[0]]) / R.d[f[0]];
        var q = [R.o[0] + R.d[0] * tt, R.o[1] + R.d[1] * tt, R.o[2] + R.d[2] * tt];
        var inside = f[0] === 0 ? (q[2] < 0 && q[2] > -2 * HZ) : (q[0] > -HX && q[0] < HX);
        if (inside && q[1] > 0 && q[1] < top && (!best || tt < best.t)) best = { face: 'side', y: q[1], t: tt };
      });
      if (best && best.face === 'side') {
        var i, k, b, y;
        for (i = 0; i < bodies.length; i++) {
          b = bodies[i];
          for (k = 0; k < b.slabs.length; k++) {
            y = b.y + (b.slabs[k].off + b.slabs[k].t) * (1 - b.s);
            if (best.y <= y) { best.slab = b.slabs[k]; return best; }
          }
        }
        return null;
      }
      return best;
    }

    /* ---------- drawing ---------- */
    function bindMesh(m, prog) {
      gl.bindBuffer(gl.ARRAY_BUFFER, m.v);
      gl.enableVertexAttribArray(prog.a.aP); gl.vertexAttribPointer(prog.a.aP, 3, gl.FLOAT, false, 28, 0);
      gl.enableVertexAttribArray(prog.a.aN); gl.vertexAttribPointer(prog.a.aN, 3, gl.FLOAT, false, 28, 12);
      if (prog.a.aU != null && prog.a.aU >= 0) { gl.enableVertexAttribArray(prog.a.aU); gl.vertexAttribPointer(prog.a.aU, 1, gl.FLOAT, false, 28, 24); }
    }
    function drawBody(b, index, list, shares) {
      var P = progs.slab, u = P.u, m = bufs.slab, k, s, above = list[index + 1];
      var squash = 1 - b.s, bulge = b.s * 2.2;
      var gap = above ? Math.max(0, above.y - (b.y + b.t * squash)) : 1e3;
      var pillow = b.isTop && model && model.pillow ? 0.7 : 0;
      for (k = 0; k < b.slabs.length; k++) {
        s = b.slabs[k];
        var last = k === b.slabs.length - 1, kind = s.kind, tint = COL[TINT[kind]] || COL.foam;
        gl.uniform4f(u.uBody, b.y + s.off * squash, s.t * squash, pillow, bulge);
        gl.uniform1i(u.uMat, MAT[kind] != null ? MAT[kind] : 1);
        gl.uniform3fv(u.cA, tint);
        gl.uniform3fv(u.cB, COL.bead);
        gl.uniform3fv(u.cC, COL.gelmem);
        gl.uniform1f(u.uAlpha, b.alpha);
        gl.uniform1f(u.uGap, last ? gap : 1e3);
        gl.uniform2f(u.uEdge, b.isTop && last ? 1 : 0, index === 0 && k === 0 && b.state !== 'leave' ? 1 : 0);
        gl.uniform2f(u.uShare, shares ? shares[index][k][0] : 0, shares ? shares[index][k][1] : 0);
        gl.uniform1f(u.uHot, hot === s ? hotA : 0);
        gl.uniform1f(u.uXray, b.isTop && last && shares ? press.xray : 0);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, m.side); gl.drawElements(gl.TRIANGLES, m.nSide, gl.UNSIGNED_SHORT, 0);
        if (last) { gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, m.top); gl.drawElements(gl.TRIANGLES, m.nTop, gl.UNSIGNED_SHORT, 0); }
      }
    }
    var hasCoils = false;
    function draw() {
      if (!gl || lost) return;
      gl.viewport(0, 0, W, Hc);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

      /* the contact shadow, softer and fainter while the first layer is still in the air */
      var first = bodies[0], lift = first ? Math.max(0, first.y) : 40, F = progs.floor;
      gl.disable(gl.DEPTH_TEST);
      gl.useProgram(F.p);
      gl.uniformMatrix4fv(F.u.uVP, false, new Float32Array(VP)); gl.uniform3fv(F.u.uFit, FIT); gl.uniform2fv(F.u.uFit2, FIT2);
      gl.uniform3fv(F.u.cShadow, COL.shadow.map(function (v) { return Math.pow(v, 1 / 2.2); }));
      gl.uniform1f(F.u.uShadow, COL.shadowA * (first ? first.alpha : 0)); gl.uniform1f(F.u.uLift, lift);
      gl.bindBuffer(gl.ARRAY_BUFFER, bufs.floor);
      gl.enableVertexAttribArray(F.a.aXZ); gl.vertexAttribPointer(F.a.aXZ, 2, gl.FLOAT, false, 0, 0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      var P = progs.slab, u = P.u;
      gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LEQUAL);
      gl.useProgram(P.p);
      gl.uniformMatrix4fv(u.uVP, false, new Float32Array(VP)); gl.uniform3fv(u.uFit, FIT); gl.uniform2fv(u.uFit2, FIT2);
      gl.uniform3fv(u.uEye, EYE); gl.uniform3fv(u.cKey, COL.key); gl.uniform3fv(u.cSky, COL.sky); gl.uniform3fv(u.cGround, COL.ground);
      gl.uniform1f(u.uExpo, COL.expo); gl.uniform3fv(u.cBlue, COL.blue); gl.uniform3fv(u.cCover, COL.cover);
      gl.uniform4f(u.uPlan, 1, 1, 0, 0);
      gl.uniform4f(u.uPress, press.x, press.z, Math.max(press.d, -0.2), 9);
      gl.uniform1f(u.uZoneK, 1);   /* press.to already carries the band's factor (zoneK) */
      gl.uniform1f(u.uZone, zoneOn && model ? model.zone : 0);
      gl.uniform1f(u.uFirm, zoneOn && model && model.firm ? 1 : 0);
      gl.uniform1f(u.uPitch, model ? model.pitch : 2.2);
      gl.uniform1f(u.uSeam, seam);
      gl.uniform2f(u.uTex, tex.has[0], tex.has[1]);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tex.knit); gl.uniform1i(u.tKnit, 0);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, tex.foam); gl.uniform1i(u.tFoam, 1);
      bindMesh(bufs.slab, P);

      /* how much of the press each surface in the stack takes, bottom up */
      var total = 0, run = 0, shares = [], i;
      hasCoils = false;
      bodies.forEach(function (b) { b.slabs.forEach(function (s) { total += GIVE[s.kind] != null ? GIVE[s.kind] : 0.1; if (isCoil(s.kind)) hasCoils = true; }); });
      bodies.forEach(function (b) {
        shares.push(b.slabs.map(function (s) { var a = run; run += (GIVE[s.kind] != null ? GIVE[s.kind] : 0.1) / (total || 1); return [a, run]; }));
      });
      gl.uniform1f(u.uCoils, hasCoils ? 1 : 0);
      for (i = 0; i < bodies.length; i++) drawBody(bodies[i], i, bodies, shares);
      for (i = 0; i < ghosts.length; i++) drawBody(ghosts[i], 0, [ghosts[i]], null);

      /* the height mark at the near corner: a post as tall as the stack has got, with a foot and a cap */
      var hgt = landedHeight();
      if (hgt > 0.05) {
        gl.uniform4f(u.uPress, 0, 0, 0, 9);
        gl.uniform1i(u.uMat, 8); gl.uniform3fv(u.cA, COL.ink); gl.uniform1f(u.uAlpha, 0.7); gl.uniform1f(u.uGap, 1e3); gl.uniform2f(u.uEdge, 0, 0); gl.uniform1f(u.uXray, 0); gl.uniform1f(u.uHot, 0);
        bindMesh(bufs.post, P);
        [[0.16, 0.16, 0, hgt], [2.4, 0.16, 0, 0.16], [2.4, 0.16, hgt - 0.16, 0.16]].forEach(function (b) {
          gl.uniform4f(u.uPlan, b[0], b[1], -HX - 3.2, 2.6);
          gl.uniform4f(u.uBody, b[2], b[3], 0, 0);
          gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, bufs.post.side); gl.drawElements(gl.TRIANGLES, bufs.post.nSide, gl.UNSIGNED_SHORT, 0);
          gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, bufs.post.top); gl.drawElements(gl.TRIANGLES, bufs.post.nTop, gl.UNSIGNED_SHORT, 0);
        });
      }
      /* the readout follows the stack up; written only when it changes */
      var r = Math.round(hgt * 100) / 100;
      if (r !== shown && dimN) { shown = r; setText(dimN, inches(r)); if (dim.hidden) dim.hidden = false; }
    }

    /* ---------- the loop: runs while something moves, then sleeps ---------- */
    var raf = 0, last = 0, acc = 0, quiet = 0, onScreen = true, drawn = false;
    function frame(t) {
      raf = 0;
      if (lost) return;
      var dt = Math.min(0.05, (t - (last || t)) / 1000); last = t;
      acc = Math.min(acc + dt, STEP * 14);
      var settled = true;
      while (acc >= STEP) { settled = step(STEP); acc -= STEP; }
      draw();
      if (!drawn) { drawn = true; stage.classList.add('is-live'); }
      quiet = settled ? quiet + dt : 0;
      if (onScreen && quiet < 0.25) raf = requestAnimationFrame(frame);
    }
    function wake() { quiet = 0; if (!raf && onScreen && !lost && gl) { last = performance.now(); raf = requestAnimationFrame(frame); } }

    /* ---------- the panel: four pickers, and what is built ---------- */
    var BUILDS = ['tight', 'euro', 'europillow', 'pillow', 'hybrid', 'foam'], FEELS = ['soft', 'medium', 'firm', 'ultra-firm'];
    var ui = panel ? {
      tier: panel.querySelector('[data-c-tier]'), name: panel.querySelector('[data-c-name]'), spec: panel.querySelector('[data-c-spec]'),
      now: panel.querySelector('[data-c-now]'), was: panel.querySelector('[data-c-was]'), forSize: panel.querySelector('[data-c-for]'),
      row: panel.querySelector('[data-c-row]'), url: panel.querySelector('[data-c-url]'), full: panel.querySelector('[data-c-full]'),
      status: panel.querySelector('[data-c-status]'), opts: panel.querySelector('[data-opts]'), groups: {}
    } : null;
    var dim = stage.querySelector('[data-dim]'), dimN = stage.querySelector('[data-dim-n]'), call = stage.querySelector('[data-call]');
    var tiers = [], buildNames = {}, feelNames = {};
    catalog.forEach(function (r) {
      if (tiers.indexOf(r.tier) < 0) tiers.push(r.tier);
      r.versions.forEach(function (v) { buildNames[v.build] = v.buildName; feelNames[v.feel] = v.feelName; });
    });
    function size() {
      var on = root.querySelector('[data-sizes] [aria-pressed="true"]');
      return on ? on.getAttribute('data-size') : root.getAttribute('data-default-size') || 'Queen';
    }
    function feelIndex(f) { return FEELS.indexOf(f); }
    /* the version of a row nearest to a top and a feel */
    function nearest(row, build, feel) {
      var best = null, score = 1e9;
      row.versions.forEach(function (v) {
        var s = (v.build === build ? 0 : 10) + Math.abs(feelIndex(v.feel) - feelIndex(feel)) + (v.build === build ? 0 : BUILDS.indexOf(v.build) * 0.01);
        if (s < score) { score = s; best = v; }
      });
      return best;
    }
    function chip(label, value, group) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'mo-sl-chip'; b.textContent = label;
      b.setAttribute('data-val', value); b.setAttribute('aria-pressed', 'false');
      group.appendChild(b);
      return b;
    }
    function shortTier(t) { var w = t.split(' '); return w.length > 1 ? t.replace(w[0] + ' ', '') : t; }
    function buildGroups() {
      if (!ui || !ui.opts) return;
      all('[data-opt]', ui.opts).forEach(function (g) { ui.groups[g.getAttribute('data-opt')] = g; });
      tiers.forEach(function (t) { chip(shortTier(t), t, ui.groups.tier); });
      BUILDS.forEach(function (b) { if (buildNames[b]) chip(buildNames[b], b, ui.groups.build); });
      FEELS.forEach(function (f) { if (feelNames[f]) chip(feelNames[f], f, ui.groups.feel); });
      ui.opts.hidden = false;
      ui.opts.addEventListener('click', function (e) {
        var b = e.target.closest('.mo-sl-chip');
        if (!b || b.disabled || !current) return;
        var kind = b.parentNode.getAttribute('data-opt'), val = b.getAttribute('data-val'), row = current.row, v = current.v, to = null;
        if (kind === 'tier') {
          if (val === row.tier) return;
          var rows = catalog.filter(function (r) { return r.tier === val; }), bestRow = null, bestV = null, score = 1e9;
          rows.forEach(function (r, i) {
            var c = nearest(r, v.build, v.feel);
            var s = (c.build === v.build ? 0 : 10) + Math.abs(feelIndex(c.feel) - feelIndex(v.feel)) + i * 0.01;
            if (s < score) { score = s; bestRow = r; bestV = c; }
          });
          if (bestRow) { row = bestRow; to = bestV; }
        } else if (kind === 'model') {
          row = catalog.filter(function (r) { return r.id === val; })[0] || row; to = nearest(row, v.build, v.feel);
        } else if (kind === 'build') {
          to = nearest(row, val, v.feel);
        } else {
          to = row.versions.filter(function (x) { return x.build === v.build && x.feel === val; })[0];
        }
        if (!to) return;
        touched = true;
        choose(row, to, true);
        /* keep the keyboard on the chip just pressed (the model row is rebuilt when the tier changes) */
        var again = ui.groups[kind].querySelector('[aria-pressed="true"]');
        if (again && document.activeElement !== again) again.focus({ preventScroll: true });
      });
    }
    function renderPanel(announce) {
      if (!ui || !current) return;
      var row = current.row, v = current.v, g = ui.groups, s = size(), entry = v.sizes[s];
      setText(ui.tier, row.tier);
      setText(ui.name, row.model || row.name);
      setText(ui.spec, v.buildName + ', ' + v.feelName.toLowerCase() + ' · ' + inches(v.h) + ' in. tall' + (v.note ? ' · ' + v.note : ''));
      setText(ui.now, entry ? money(entry.price) : 'Not carried in ' + s);
      if (ui.now) ui.now.classList.toggle('is-none', !entry);
      setText(ui.was, entry && entry.compare > entry.price ? money(entry.compare) : '');
      setText(ui.forSize, entry ? s : '');
      if (ui.row && ui.row.getAttribute('href') !== '#' + row.id) ui.row.setAttribute('href', '#' + row.id);
      if (ui.url && v.url && ui.url.getAttribute('href') !== v.url) ui.url.setAttribute('href', v.url);
      setText(ui.full, row.name + ' ' + v.key + ' ');
      if (g.tier) {
        all('.mo-sl-chip', g.tier).forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-val') === row.tier)); });
        var ids = catalog.filter(function (r) { return r.tier === row.tier; });
        if (g.model.getAttribute('data-tier') !== row.tier) {
          g.model.setAttribute('data-tier', row.tier);
          g.model.textContent = '';
          ids.forEach(function (r) { chip(r.model || r.name, r.id, g.model); });
        }
        all('.mo-sl-chip', g.model).forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-val') === row.id)); });
        all('.mo-sl-chip', g.build).forEach(function (b) {
          var val = b.getAttribute('data-val'), made = row.versions.some(function (x) { return x.build === val; });
          b.disabled = !made; b.setAttribute('aria-pressed', String(val === v.build));
          if (made) b.removeAttribute('title'); else b.title = 'Not carried here in this model';
        });
        all('.mo-sl-chip', g.feel).forEach(function (b) {
          var val = b.getAttribute('data-val'), made = row.versions.some(function (x) { return x.build === v.build && x.feel === val; });
          b.disabled = !made; b.setAttribute('aria-pressed', String(val === v.feel));
          if (made) b.removeAttribute('title'); else b.title = 'Not carried here in this build';
        });
      }
      if (announce) setText(ui.status, row.name + ', ' + v.key + ', ' + inches(v.h) + ' inches tall, ' + (entry ? money(entry.price) + ' for ' + s : 'not carried in ' + s));
    }
    function choose(row, v, animate) {
      current = { row: row, v: v };
      setStack(stackOf(v), animate && !still && !!gl, 0.15);
      hot = null; hideCall();
      renderPanel(animate);
      if (!gl && !lazy()) { toStart(true); return; }
      if (still || !animate) { draw(); if (!drawn && gl) { drawn = true; stage.classList.add('is-live'); } } else wake();
    }
    /* "See it in the lineup": open the row on the version built here */
    function openRow(e) {
      if (!current) return;
      var row = current.row, v = current.v, shownV = row.el.querySelector('.mo-lineup__v:not([hidden])');
      e.preventDefault();
      if (row.el.hidden) {
        var allBtn = root.querySelector('[data-feels] [data-feel="all"]'), floor = root.querySelector('[data-floor-only][aria-pressed="true"]');
        if (allBtn && allBtn.getAttribute('aria-pressed') !== 'true') allBtn.click();
        if (floor) floor.click();
      }
      if (shownV !== v.el) {
        var btn = row.el.querySelector('[data-pick="' + v.i + '"]');
        if (btn) btn.click();
      }
      row.el.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
      if (history.replaceState) history.replaceState(null, '', '#' + row.id);
      row.el.classList.remove('is-target'); void row.el.offsetWidth; row.el.classList.add('is-target');
    }

    /* ---------- naming a layer, pressing the bed ---------- */
    function showCall(slab) {
      if (!call || !slab || !slab.name) return hideCall();
      setText(call, slab.name);
      if (call.hidden) call.hidden = false;
    }
    function hideCall() { if (call && !call.hidden) call.hidden = true; }
    var down = null, tap = null, touched = false, zoneTimer = 0, zonePending = '';
    /* the band's name, in Sealy's words, while the bed is pressed */
    function nameZone() {
      clearTimeout(zoneTimer);
      if (zonePending) { hot = null; showCall({ name: zonePending }); zoneCall = true; zonePending = ''; }
    }
    function onDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      var p = pick(e.clientX, e.clientY);
      if (!p) { hot = null; hideCall(); wake(); return; }
      /* a finger that lands on the bed may be starting a scroll: it counts as a touch once it lifts
         (release) or has stayed down a moment, not before */
      var finger = e.pointerType === 'touch';
      if (!finger) touched = true;
      if (p.face === 'side') {
        /* a finger names the layer when it lifts (onUp), so a scroll that starts here names nothing */
        if (finger) { tap = { id: e.pointerId, slab: p.slab }; return; }
        touched = true; hot = hot === p.slab && e.pointerType !== 'mouse' ? null : p.slab; if (hot) showCall(hot); else hideCall(); wake(); return;
      }
      if (still) return;
      down = { id: e.pointerId, t: performance.now() };
      press.x = clamp(p.x, -HX + 5, HX - 5); press.z = clamp(p.z, -2 * HZ + 5, -5); press.on = true; press.to = 2.7 * zoneK(press.z);
      try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      stage.classList.add('is-pressing');
      zonePending = (zoneOn && model && zoneNames[model.zone]) || '';
      clearTimeout(zoneTimer);
      if (finger) zoneTimer = setTimeout(function () { if (down) { touched = true; nameZone(); } }, 160);
      else nameZone();
      wake();
    }
    /* the browser took the gesture (a scroll): end the press at once, with no hold and no name */
    function cancelPress() {
      clearTimeout(zoneTimer); zonePending = ''; tap = null;
      if (!down) return;
      down = null; press.on = false; press.to = 0; stage.classList.remove('is-pressing');
      if (zoneCall) { zoneCall = false; hideCall(); }
      wake();
    }
    function onMove(e) {
      if (down) {
        var p = pick(e.clientX, e.clientY);
        if (p && p.face === 'top') { press.x = clamp(p.x, -HX + 5, HX - 5); press.z = clamp(p.z, -2 * HZ + 5, -5); press.to = 2.7 * zoneK(press.z); wake(); }
        return;
      }
      if (e.pointerType !== 'mouse') return;
      var h = pick(e.clientX, e.clientY), slab = h && h.face === 'side' ? h.slab : null;
      if (slab !== hot) { hot = slab; if (hot) showCall(hot); else hideCall(); wake(); }
    }
    function release(e) {
      if (tap && e && e.type === 'pointerup' && e.pointerId === tap.id) {
        touched = true; hot = hot === tap.slab ? null : tap.slab; tap = null;
        if (hot) showCall(hot); else hideCall();
        wake(); return;
      }
      tap = null;
      if (!down) return;
      touched = true; nameZone();
      /* a quick tap still reads as a press: hold it a moment before letting go */
      var held = performance.now() - down.t;
      down = null;
      setTimeout(function () { if (!down) { press.on = false; press.to = 0; stage.classList.remove('is-pressing'); if (zoneCall) { zoneCall = false; hideCall(); } wake(); } }, held < 260 ? 320 - held : 0);
    }
    function build(delay) {
      /* take the bed apart and build it again, bottom up */
      if (!current || still || !gl) return;
      var next = stackOf(current.v), old = bodies;
      old.forEach(function (b) { b.state = 'leave'; b.vy = 20; ghosts.push(b); });
      bodies = [];
      setStack(next, true, 0.3);
      bodies.forEach(function (b) { b.release += delay || 0; b.born += delay || 0; });
      wake();
    }

    /* ---------- start ---------- */
    /* With no drawing (no WebGL, or the context is lost) the still shows, and the still is the bed
       built first. The panel goes back to that bed and the pickers hide, so picture and words agree. */
    function toStart(hide) {
      if (ui && ui.opts) ui.opts.hidden = !!hide;
      if (!startRow || !current || current.v === startV) return;
      current = { row: startRow, v: startV };
      setStack(stackOf(startV), false);
      hot = null; hideCall();
      renderPanel(false);
      if (ui) setText(ui.status, '');
    }
    var started = false;
    function lazy() {
      /* WebGL starts on demand: at once normally, on the first pick when the visitor asked to save data */
      if (gl || started) return !!gl;
      started = true;
      if (!context() || !setup()) { gl = null; stage.classList.add('is-flat'); toStart(true); return false; }
      readColors(); resize();
      loadTexture('knit', 0); loadTexture('foam', 1);
      canvas.addEventListener('pointerdown', onDown);
      canvas.addEventListener('pointermove', onMove);
      canvas.addEventListener('pointerup', release);
      canvas.addEventListener('pointercancel', cancelPress);
      canvas.addEventListener('contextmenu', cancelPress);
      canvas.addEventListener('lostpointercapture', release);
      /* a mouse that leaves the bed drops the name; a finger "leaves" the moment it lifts, and the name it asked for stays */
      canvas.addEventListener('pointerleave', function (e) { if (e.pointerType === 'touch') return; if (hot) { hot = null; hideCall(); wake(); } });
      canvas.addEventListener('webglcontextlost', function (e) { e.preventDefault(); lost = true; if (raf) { cancelAnimationFrame(raf); raf = 0; } stage.classList.remove('is-live'); drawn = false; cancelPress(); toStart(true); });
      canvas.addEventListener('webglcontextrestored', function () { lost = false; if (setup()) { readColors(); resize(); loadTexture('knit', 0); loadTexture('foam', 1); toStart(false); wake(); } });
      return true;
    }

    buildGroups();
    var startRow = catalog.filter(function (r) { return r.id === stage.getAttribute('data-start-row'); })[0] || catalog[0];
    var startV = startRow ? (startRow.versions.filter(function (v) { return v.url === stage.getAttribute('data-start-url'); })[0] || startRow.versions[0]) : null;
    if (!startRow) { stage.classList.add('is-flat'); return function () {}; }
    current = { row: startRow, v: startV };
    setStack(stackOf(startV), false);
    renderPanel(false);
    if (ui && ui.row) ui.row.addEventListener('click', openRow);
    /* the price follows the size picker; the shared script settles the size first */
    root.addEventListener('click', function (e) { if (e.target.closest('[data-size]')) setTimeout(function () { renderPanel(false); }, 0); });

    var again = stage.querySelector('[data-again]');
    if (!saver) lazy();
    if (gl) {
      if (again && !still) { again.hidden = false; again.addEventListener('click', function () { touched = true; build(0); }); }
      var introDone = false;
      var begin = function (visible) {
        onScreen = visible;
        if (!visible) return;
        wake();
        if (still) { draw(); if (!drawn) { drawn = true; stage.classList.add('is-live'); } return; }
        /* one orchestrated build the first time the bed is on screen, then it waits */
        if (!introDone && !window.__moSlNoIntro) {
          introDone = true;
          setTimeout(function () { if (!touched && onScreen) build(0.1); }, 900);
        }
      };
      if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { begin(es[es.length - 1].isIntersecting); }, { threshold: 0.3 }).observe(canvas);
      else begin(true);
    }

    window.__moSl = {
      catalog: catalog, state: function () { return { row: current.row.id, key: current.v.key, h: current.v.h, top: stackTop(), landed: landedHeight(), seam: seam, bodies: bodies.map(function (b) { return { cls: b.cls, state: b.state, y: b.y, t: b.t, s: b.s, slabs: b.slabs.map(function (s) { return s.kind + ':' + s.t.toFixed(3) + ':' + (s.inch || 0); }) }; }), ghosts: ghosts.length, press: { d: press.d, x: press.x, z: press.z, on: press.on }, zone: model.zone, zoneOn: zoneOn, asleep: !raf, live: drawn, gl: !!gl }; },
      choose: function (rowId, key, animate) { var r = catalog.filter(function (x) { return x.id === rowId; })[0]; var v = r && r.versions.filter(function (x) { return x.key === key; })[0]; if (v) choose(r, v, animate !== false); return !!v; },
      build: function () { build(0); }, pick: pick,
      pressAt: function (x, z, on) { press.x = x; press.z = z; press.on = !!on; press.to = on ? 2.7 * zoneK(z) : 0; wake(); },
      project: function (p) { var q = xform(VP, p), r = canvas.getBoundingClientRect(); var nx = (q[0] * FIT[0] + FIT[1]) * FIT2[0], ny = (q[1] * FIT[0] + FIT[2]) * FIT2[1]; return [r.left + (nx + 1) / 2 * r.width, r.top + (1 - ny) / 2 * r.height]; },
      settle: function () { var i; for (i = 0; i < 4000; i++) step(STEP); seam = 1; draw(); },
      draw: draw
    };

    return function repaint() {
      if (!gl) return;
      readColors(); resize();
      if (!raf) draw();
      wake();
    };
  };

  /* ==================================================================================
   * Row drawing: a Sealy side view with the cover cut back
   * Height is to scale, 5 units per inch, like every lineup drawing, and each layer is drawn at the
   * depth Sealy publishes (an "in." in the layer name), by the same rule as the showpiece.
   * Kinds: cover, chill (covers); foam, gel, gelmem, mem, support, core, base; coil (encased coils),
   * coil-third (the named coils sit in the center third), coil-max (an extra set of coils in the
   * center). "top" puts a layer in the pillow top. The border is the bed's white knit with one
   * stitched line of Sealy blue.
   * ================================================================================== */
  var cutN = 0;
  function n1(v) { return Math.round(v * 10) / 10; }
  draws.sealy = function (version, tall, kinds) {
    var lis = version ? all('.mo-lineup__layers li', version) : [];
    var src = lis.length ? lis.map(function (li) {
      var bits = (li.getAttribute('data-kind') || '').trim().split(/\s+/), text = li.textContent || '', m = /(\d+(?:\.\d+)?)\s*in\./i.exec(text);
      return { kind: bits[0], top: bits.indexOf('top') > 0, inch: m ? parseFloat(m[1]) : 0, name: text };
    }) : kinds.map(function (k) { var bits = k.split(/\s+/); return { kind: bits[0], top: bits.indexOf('top') > 0, inch: 0, name: '' }; });
    src = src.filter(function (l) { return l.kind; });
    var list = depths(src, tall);
    var build = version ? version.getAttribute('data-build') : '';
    var id = 'mo-scut-' + (++cutN);
    var VW = 204, VH = 126, X0 = 12, X1 = 198, FLOOR = 115, BOTTOM = FLOOR - 0.5;
    var h = Math.max(48, Math.min(tall * 5, 92)), U = h / tall, top = BOTTOM - h;
    var CUT = X0 + (X1 - X0) * 0.27, slope = 0.16;
    function cx(y) { return CUT + (y - top) * slope; }
    var Z0 = X0 + (X1 - X0) / 3, Z1 = X0 + (X1 - X0) * 2 / 3;
    var cover = list.filter(function (l) { return isCover(l.kind); })[0];
    var body = list.filter(function (l) { return l !== cover; });
    var COV = cover ? Math.max(1.8, cover.t * U) : 0, ptH = 0;
    body.forEach(function (l) { l.hh = l.t * U; if (l.top) ptH += l.hh; });

    var o = '<defs><clipPath id="' + id + 'c"><path d="M' + n1(cx(top)) + ',' + n1(top) + 'H' + (X1 - 3) + 'Q' + X1 + ',' + n1(top) + ' ' + X1 + ',' + n1(top + 3) +
      'V' + n1(BOTTOM - 2) + 'Q' + X1 + ',' + n1(BOTTOM) + ' ' + (X1 - 2) + ',' + n1(BOTTOM) + 'H' + n1(cx(BOTTOM)) + 'Z"/></clipPath></defs>';
    o += '<ellipse class="s-shadow" cx="' + ((X0 + X1) / 2) + '" cy="' + (FLOOR + 2.2) + '" rx="' + ((X1 - X0) / 2 + 4) + '" ry="2.2"/>';
    o += '<path class="s-dim" d="M5,' + n1(top) + 'V' + n1(BOTTOM) + 'M2.4,' + n1(top) + 'h5.2M2.4,' + n1(BOTTOM) + 'h5.2"/>';

    /* each layer carries the bed's outline as its own clip, so the clip moves with a layer that lifts */
    var CLIP = ' clip-path="url(#' + id + 'c)"';
    o += '<g>';
    var y = top + COV, n = body.length;
    body.forEach(function (l, idx) {
      var y0 = y, y1 = y + l.hh, k = l.kind, xs = cx(y0) + 2.5, xe = X1 - 2.5, yy, xx, r;
      var coil = isCoil(k), lifts = !coil && k !== 'base', cls = coil ? 'coil' : k === 'center' ? 'foam' : k;
      o += '<g' + CLIP + ' class="' + (lifts ? 'c-lift ' : '') + 's-l s-' + cls + '"' + (lifts ? ' style="--lift:' + n1((n - idx) * 3) + '"' : '') + '>';
      o += '<rect class="s-fill" x="' + X0 + '" y="' + n1(y0) + '" width="' + (X1 - X0) + '" height="' + n1(Math.max(0.6, l.hh - 0.4)) + '"/>';
      if (coil) {
        /* encased coils: each spring in its own fabric pocket, at one spacing from head to foot.
           The center third is marked where Sealy names it. Where Sealy adds an extra set of coils
           in the center (no width given), slim blue coils sit between the main ones. */
        var zoned = k === 'coil-third', nested = k === 'coil-max', pitch = 6.2, rail = 5.5, c0 = xs, py0 = y0 + 0.8, py1 = y1 - 0.8, nest = '';
        if (zoned) {
          o += '<rect class="s-zonefill" x="' + n1(Math.max(Z0, xs)) + '" y="' + n1(y0 + 0.4) + '" width="' + n1(Z1 - Math.max(Z0, xs)) + '" height="' + n1(l.hh - 1.2) + '"/>';
        }
        while (c0 < xe - rail - 1) {
          var w = pitch;
          if (nested && c0 > Z0 - 3 && c0 < Z1 + 3 && c0 > xs + 1) nest += '<rect class="s-nest" x="' + n1(c0 - 0.75) + '" y="' + n1(py0 + 1.1) + '" width="1.5" height="' + n1(py1 - py0 - 2.2) + '" rx="0.75"/>';
          if (c0 + w > xe - rail) w = xe - rail - c0;
          if (w < 2.2) break;
          var mid = c0 + w / 2;
          o += '<rect class="s-pocket" x="' + n1(c0 + 0.3) + '" y="' + n1(py0) + '" width="' + n1(w - 0.6) + '" height="' + n1(py1 - py0) + '" rx="1.8"/>';
          var turns = 5, span = py1 - py0 - 2.4, rx = w * 0.3, ry = Math.min(1.1, span / turns * 0.3), back = '', front = '', on = -1, steps = turns * 14, j;
          for (j = 0; j <= steps; j++) {
            var an = j / steps * turns * Math.PI * 2, hx = mid + Math.cos(an) * rx, hy = py0 + 1.2 + span * j / steps + Math.sin(an) * ry, near = Math.sin(an) >= 0 ? 1 : 0;
            var pt = n1(hx) + ',' + n1(hy);
            if (near !== on) { if (near) front += 'M' + pt; else back += 'M' + pt; on = near; }
            else if (near) front += 'L' + pt; else back += 'L' + pt;
          }
          o += '<path class="s-wire s-wire--back" d="' + back + '"/><path class="s-wire" d="' + front + '"/>';
          c0 += w;
        }
        o += '<rect class="s-rail" x="' + n1(xe - rail + 0.4) + '" y="' + n1(y0 + 0.5) + '" width="' + n1(rail - 0.4) + '" height="' + n1(l.hh - 1) + '" rx="1"/>';
        o += nest;
        if (zoned) o += '<path class="s-zoneline" d="M' + n1(Math.max(Z0, xs)) + ',' + n1(y0 + 0.4) + 'V' + n1(y1 - 0.6) + 'M' + n1(Z1) + ',' + n1(y0 + 0.4) + 'V' + n1(y1 - 0.6) + '"/>';
      } else {
        /* a center support layer: gel memory foam through the center third only */
        if (k === 'center') o += '<rect class="s-centerfill" x="' + n1(Math.max(Z0, cx(y0))) + '" y="' + n1(y0) + '" width="' + n1(Z1 - Math.max(Z0, cx(y0))) + '" height="' + n1(Math.max(0.6, l.hh - 0.4)) + '"/>';
        /* foams: an open grain; the gel foams carry beads */
        var gap = k === 'gelmem' ? 3.4 : k === 'gel' ? 4.4 : k === 'mem' ? 3 : k === 'base' ? 5.6 : 4.8, rad = k === 'gel' || k === 'gelmem' ? 0.55 : 0.4;
        for (yy = y0 + 1.2, r = 0; yy < y1 - 0.6; yy += gap * 0.6, r++)
          for (xx = xs + (r % 2) * gap / 2 + 0.6; xx < xe; xx += gap) o += '<circle class="s-dot" cx="' + n1(xx + (((xx * 13.7 + yy * 7.3) % 1.2) - 0.6) * (k === 'gel' || k === 'gelmem' ? 0 : 1)) + '" cy="' + n1(yy) + '" r="' + rad + '"/>';
      }
      o += '<path class="s-seam" d="M' + n1(cx(y1)) + ',' + n1(y1 - 0.2) + 'H' + X1 + '"/>';
      o += '</g>';
      y = y1;
    });
    /* the cover over the cut: a quilted knit lid that lifts with the layers */
    if (cover) {
      o += '<g' + CLIP + ' class="c-lift s-l s-' + cover.kind + ' s-cover" style="--lift:' + n1(n * 3 + 4) + '">';
      o += '<path class="s-fill" d="M' + X0 + ',' + n1(top + 0.6) + 'Q' + X0 + ',' + n1(top) + ' ' + (X0 + 2) + ',' + n1(top) + 'H' + (X1 - 3) + 'Q' + X1 + ',' + n1(top) + ' ' + X1 + ',' + n1(top + 2) + 'V' + n1(top + COV - 0.3) + 'H' + X0 + 'Z"/>';
      var pq = 'M' + n1(cx(top) + 2) + ',' + n1(top + 1.7), qx;
      for (qx = cx(top) + 2; qx < X1 - 7; qx += 6.5) pq += 'Q' + n1(qx + 3.25) + ',' + n1(top - 0.5) + ' ' + n1(qx + 6.5) + ',' + n1(top + 1.7);
      o += '<path class="s-puff" d="' + pq + '"/>';
      o += '<path class="s-thread" d="M' + n1(cx(top + COV - 0.5)) + ',' + n1(top + COV - 0.5) + 'H' + (X1 - 0.8) + '"/>';
      o += '</g>';
    }
    o += '</g>';

    /* the finished side, left of the cut: the white knit border, a crown on pillow tops, the tape
       edges and the blue thread */
    var pillow = (build === 'europillow' || build === 'pillow') && ptH > 2, band = top + COV + (pillow ? ptH : build === 'euro' && ptH > 1 ? ptH : 2);
    if (pillow) {
      o += '<path class="s-crown" d="M' + (X0 + 1) + ',' + n1(band) + 'C' + (X0 - 1.6) + ',' + n1(band - (band - top) * 0.35) + ' ' + (X0 - 1.6) + ',' + n1(top + 3) + ' ' + (X0 + 2.6) + ',' + n1(top) + 'H' + n1(cx(top)) + 'L' + n1(cx(band)) + ',' + n1(band) + 'Z"/>';
      o += '<path class="s-wave" d="M' + (X0 + 2.5) + ',' + n1(top + (band - top) * 0.52) + 'H' + n1(cx(top + (band - top) * 0.52) - 1) + '"/>';
    } else {
      o += '<path class="s-crown" d="M' + (X0 + 2) + ',' + n1(top) + 'H' + n1(cx(top)) + 'L' + n1(cx(band)) + ',' + n1(band) + 'H' + X0 + 'V' + n1(top + 2) + 'Q' + X0 + ',' + n1(top) + ' ' + (X0 + 2) + ',' + n1(top) + 'Z"/>';
    }
    var pb = band + (pillow ? 0.9 : 0);
    o += '<path class="s-panel" d="M' + X0 + ',' + n1(pb) + 'H' + n1(cx(pb)) + 'L' + n1(cx(BOTTOM)) + ',' + n1(BOTTOM) + 'H' + (X0 + 3) + 'Q' + X0 + ',' + n1(BOTTOM) + ' ' + X0 + ',' + n1(BOTTOM - 3) + 'Z"/>';
    for (var bx = X0 + 3; bx < cx(BOTTOM) - 1.5; bx += 3) {
      var rt = Math.max(pb + 4.2, top + (bx - CUT) / slope + 1);
      if (rt < BOTTOM - 4) o += '<path class="s-rib" d="M' + n1(bx) + ',' + n1(rt) + 'V' + n1(BOTTOM - 3.2) + '"/>';
    }
    o += '<path class="s-tape" d="M' + (X0 + 0.5) + ',' + n1(pb) + 'H' + n1(cx(pb) - 0.3) + 'M' + (X0 + 1.5) + ',' + n1(BOTTOM - 0.6) + 'H' + n1(cx(BOTTOM - 0.6) - 0.3) + '"/>';
    o += '<path class="s-thread" d="M' + (X0 + 0.8) + ',' + n1(pb + 2) + 'H' + n1(cx(pb + 2) - 0.5) + 'M' + (X0 + 1.6) + ',' + n1(BOTTOM - 2.5) + 'H' + n1(cx(BOTTOM - 2.5) - 0.5) + '"/>';
    o += '<path class="s-edge" d="M' + n1(cx(top)) + ',' + n1(top) + 'L' + n1(cx(BOTTOM)) + ',' + n1(BOTTOM) + '"/>';
    o += '<path class="s-floor" d="M1,' + (FLOOR + 0.6) + 'H' + (VW - 1) + '"/>';
    return '<svg viewBox="0 0 ' + VW + ' ' + VH + '" aria-hidden="true" focusable="false">' + o + '</svg>';
  };

  /* ==================================================================================
   * The ladder, the row pickers and the floor filter
   * ================================================================================== */
  function sizesOf(v) { try { return JSON.parse(v.querySelector('[data-sizes-json]').textContent); } catch (e) { return {}; } }
  function bootLadder(root) {
    if (root.__moSlLadder) return;
    root.__moSlLadder = true;
    var cells = all('[data-ladder-cell]', root);
    function cellVersions(cell) {
      var row = document.getElementById(cell.getAttribute('data-target'));
      return row ? all('.mo-lineup__v[data-build="' + cell.getAttribute('data-build') + '"]', row) : [];
    }
    cells.forEach(function (cell) { cell.__sizes = cellVersions(cell).map(sizesOf); });
    function size() {
      var on = root.querySelector('[data-sizes] [aria-pressed="true"]');
      return on ? on.getAttribute('data-size') : root.getAttribute('data-default-size') || 'Queen';
    }
    function price() {
      var s = size();
      cells.forEach(function (cell) {
        var low = 0;
        cell.__sizes.forEach(function (map) { var e = map[s]; if (e && (!low || e.price < low)) low = e.price; });
        setText(cell.querySelector('[data-cell-price]'), low ? 'from ' + money(low) : 'Not carried in ' + s);
        cell.classList.toggle('is-out', !low);
      });
    }
    /* the shared script settles the size first; read it once the click has run */
    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-size]')) setTimeout(price, 0);
      var pick = e.target.closest('.mo-lineup__row--series [data-pick]');
      if (pick && e.isTrusted !== false) {
        /* keep the keyboard on the button just pressed, in the version that now shows */
        var group = pick.closest('[role="group"]'), label = group && group.getAttribute('aria-label'), text = pick.textContent, row = pick.closest('[data-row]');
        setTimeout(function () {
          var v = row && row.querySelector('.mo-lineup__v:not([hidden])');
          var g = v && all('[role="group"]', v).filter(function (x) { return x.getAttribute('aria-label') === label; })[0];
          var b = g && all('button', g).filter(function (x) { return x.textContent === text; })[0];
          if (b && document.activeElement !== b && row.contains(document.activeElement)) b.focus({ preventScroll: true });
        }, 0);
      }
      var cell = e.target.closest('[data-ladder-cell]');
      if (!cell) return;
      var target = document.getElementById(cell.getAttribute('data-target'));
      if (!target) return;
      e.preventDefault();
      var build = cell.getAttribute('data-build');
      var shown = target.querySelector('.mo-lineup__v:not([hidden])');
      if (!shown || shown.getAttribute('data-build') !== build) {
        var versions = all('.mo-lineup__v', target);
        var btn = shown && all('[data-pick]', shown).filter(function (b) { var v = versions[+b.getAttribute('data-pick')]; return v && v.getAttribute('data-build') === build && / build$/.test(b.closest('[role="group"]').getAttribute('aria-label')); })[0];
        if (btn) btn.click();
        else {
          /* the row is filtered out or shows another build: open its first version of this build */
          var first = versions.filter(function (v) { return v.getAttribute('data-build') === build; })[0];
          var any = first && target.querySelector('[data-pick="' + versions.indexOf(first) + '"]');
          if (any) any.click();
        }
      }
      if (target.hidden) {
        /* a filter hides the row: clear it so the row shows */
        var allBtn = root.querySelector('[data-feels] [data-feel="all"]'), floor = root.querySelector('[data-floor-only][aria-pressed="true"]');
        if (allBtn && allBtn.getAttribute('aria-pressed') !== 'true') allBtn.click();
        if (floor && target.hidden) floor.click();
      }
      target.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
      if (history.replaceState) history.replaceState(null, '', '#' + target.id);
      target.classList.remove('is-target'); void target.offsetWidth; target.classList.add('is-target');
    });
    price();
    /* The shared script prints "Not made in <size>" when a bed has no variant in the chosen size.
       For Sealy that is not true: Sealy makes sizes this store does not list. The rows say
       "Not carried in <size>" instead, here and whenever the shared script rewrites the price. */
    function carried(el) {
      var t = el && el.textContent;
      if (t && t.lastIndexOf('Not made in ', 0) === 0) el.textContent = 'Not carried in ' + t.slice(12);
    }
    all('.mo-lineup__row--series [data-now]', root).forEach(carried);
    if (window.MutationObserver) {
      new MutationObserver(function (recs) {
        recs.forEach(function (r) {
          var n = r.target.nodeType === 1 ? r.target : r.target.parentNode;
          n = n && n.closest ? n.closest('[data-now]') : null;
          if (n && n.closest('.mo-lineup__row--series')) carried(n);
        });
      }).observe(root, { subtree: true, childList: true, characterData: true });
    }
    /* a page whose mattresses have no floor record has nothing for the floor filter to show */
    var toggle = root.querySelector('[data-floor-only]');
    if (toggle && !root.querySelector('[data-mo-card-tryability="exact"]')) toggle.classList.add('is-moot');
    peekLadder(root);
  }
  /* The ladder scrolls sideways under 1024 px. A column only partly in view, at the right edge or
     under the sticky build column, gets is-peek: the CSS keeps its card outlines and hides its words,
     so the peek shows the next cards without cutting a name or price mid-word (2026-10-16). */
  function peekLadder(root) {
    var box = root.querySelector('.mo-sl-ladder__scroll');
    if (!box || !window.IntersectionObserver) return;
    var parts = all('thead th:not(:first-child), tbody td', box), io = null, wait = 0;
    function watch() {
      if (io) io.disconnect();
      var lead = box.querySelector('tbody th') || box.querySelector('thead th');
      var cover = lead ? Math.max(0, Math.round(lead.getBoundingClientRect().right - box.getBoundingClientRect().left)) : 0;
      io = new IntersectionObserver(function (list) {
        list.forEach(function (e) { e.target.classList.toggle('is-peek', e.intersectionRatio < 0.98); });
      }, { root: box, rootMargin: '0px 0px 0px -' + cover + 'px', threshold: [0, 0.25, 0.5, 0.75, 0.98, 1] });
      parts.forEach(function (el) { io.observe(el); });
    }
    watch();
    window.addEventListener('resize', function () { clearTimeout(wait); wait = setTimeout(watch, 150); });
  }
  function bootAll(scope) { all('[data-mo-sealy] [data-mo-lineup]', scope).forEach(bootLadder); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { bootAll(document); });
  else bootAll(document);
  document.addEventListener('shopify:section:load', function (e) { bootAll(e.target); });
})();
